import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Camera,
  FolderOpen,
  CheckCircle2,
  Plus,
  Bell,
  BellOff,
  Pill,
  Loader2,
  AlertCircle,
  History,
  Coins,
  X,
  ScanLine,
  Upload,
  Pencil,
  Save,
  Trash2,
} from 'lucide-react';
import Navigation from '../components/layout/Navigation';
import prescriptionService, { Prescription, ScannedMedication } from '../services/prescription.service';

type ScanState = 'idle' | 'scanning' | 'result' | 'error';

interface EditableMedication extends ScannedMedication {
  _editing?: boolean;
}

export default function PrescriptionScannerPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [scanState, setScanState] = useState<ScanState>('idle');
  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [savingToProfile, setSavingToProfile] = useState(false);
  const [savedToProfile, setSavedToProfile] = useState(false);
  const [selectedMeds, setSelectedMeds] = useState<Set<number>>(new Set());
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Editable medications state (local copy for editing)
  const [editableMeds, setEditableMeds] = useState<EditableMedication[]>([]);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<EditableMedication | null>(null);

  const handleFile = useCallback(async (file: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrorMsg('Te rugăm să selectezi o imagine (JPG, PNG, HEIC)');
      setScanState('error');
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setScanState('scanning');
    setErrorMsg('');
    try {
      const result = await prescriptionService.scanPrescription(file);
      setPrescription(result.prescription);
      setReminderEnabled(result.prescription.reminderEnabled);
      const meds = result.prescription.medications.map(m => ({ ...m }));
      setEditableMeds(meds);
      setSelectedMeds(new Set(meds.map((_, i) => i)));
      setScanState('result');
    } catch (err: any) {
      if (err.status === 402) {
        setErrorMsg(`Credite insuficiente. Ai nevoie de 3 credite pentru scanare. Disponibile: ${err.available || 0}`);
      } else {
        setErrorMsg(err.error || err.message || 'Eroare la scanarea rețetei. Încearcă din nou.');
      }
      setScanState('error');
    }
  }, []);

  const handleFileInput = (e: { target: HTMLInputElement & { files: FileList | null } }) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    (e.target as HTMLInputElement).value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleReminderToggle = async () => {
    if (!prescription) return;
    const newValue = !reminderEnabled;
    setReminderEnabled(newValue);
    try {
      await prescriptionService.updatePrescription(prescription.id, { reminderEnabled: newValue });
    } catch {
      setReminderEnabled(!newValue);
    }
  };

  // Start editing a medication
  const startEdit = (idx: number) => {
    setEditingIdx(idx);
    setEditDraft({ ...editableMeds[idx] });
  };

  // Save edit
  const saveEdit = () => {
    if (editingIdx === null || !editDraft) return;
    const updated = [...editableMeds];
    updated[editingIdx] = { ...editDraft };
    setEditableMeds(updated);
    setEditingIdx(null);
    setEditDraft(null);
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingIdx(null);
    setEditDraft(null);
  };

  // Delete a medication from the list
  const deleteMed = (idx: number) => {
    const updated = editableMeds.filter((_, i) => i !== idx);
    setEditableMeds(updated);
    // Rebuild selectedMeds indices
    const newSelected = new Set<number>();
    updated.forEach((_, i) => {
      if (selectedMeds.has(i < idx ? i : i + 1)) newSelected.add(i);
    });
    setSelectedMeds(newSelected);
    if (editingIdx === idx) { setEditingIdx(null); setEditDraft(null); }
  };

  // Add a new empty medication
  const addMed = () => {
    const newMed: EditableMedication = { name: '', dose: '', quantity: '', frequency: '', notes: '' };
    const updated = [...editableMeds, newMed];
    setEditableMeds(updated);
    const newIdx = updated.length - 1;
    setSelectedMeds(prev => new Set([...prev, newIdx]));
    startEdit(newIdx);
  };

  const handleSaveToProfile = async () => {
    if (!prescription || selectedMeds.size === 0) return;
    setSavingToProfile(true);
    try {
      // First update the prescription medications with edited values
      await prescriptionService.updatePrescription(prescription.id, {
        savedToProfile: false, // will be set to true by save-to-profile endpoint
      });

      // Save selected medications (using edited values)
      await prescriptionService.saveMedicationsToProfileEdited(
        prescription.id,
        Array.from(selectedMeds).map(i => editableMeds[i]).filter(Boolean)
      );
      setSavedToProfile(true);
      setShowSaveModal(false);
    } catch (err: any) {
      alert(err.message || 'Eroare la salvare');
    } finally {
      setSavingToProfile(false);
    }
  };

  const resetScan = () => {
    setScanState('idle');
    setPrescription(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setErrorMsg('');
    setSavedToProfile(false);
    setSelectedMeds(new Set());
    setEditableMeds([]);
    setEditingIdx(null);
    setEditDraft(null);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Page Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-rose-100 rounded-xl flex items-center justify-center">
                  <ScanLine className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Scanner Rețetă</h1>
                  <p className="text-xs text-gray-500 hidden sm:block">Scanează și salvează rețetele medicale cu AI</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate('/prescription-history')}
              className="flex items-center gap-2 bg-rose-50 text-rose-600 text-sm font-semibold px-4 py-2 rounded-xl border border-rose-200 hover:bg-rose-100 transition-colors"
            >
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">Istoric Rețete</span>
              <span className="sm:hidden">Istoric</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-8">

        {/* Credit info banner */}
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6">
          <Coins className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700">
            Scanarea unei rețete costă <strong>3 credite</strong>. FII FIT AI extrage automat medicamentele, dozele și frecvența.
          </p>
        </div>

        {/* Two-column layout on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* LEFT COLUMN - Upload area */}
          <div className="space-y-4">
            <h2 className="text-base font-semibold text-gray-700">Încarcă rețeta</h2>

            {/* IDLE STATE */}
            {(scanState === 'idle' || scanState === 'error') && (
              <>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  className={`bg-white rounded-2xl border-2 border-dashed p-10 flex flex-col items-center gap-4 transition-colors ${
                    dragOver ? 'border-rose-400 bg-rose-50' : 'border-rose-200 hover:border-rose-300'
                  }`}
                >
                  <div className="w-20 h-20 bg-rose-50 rounded-2xl flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-rose-400">Rx</p>
                      <div className="w-8 h-0.5 bg-rose-300 mx-auto mt-1" />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-700">Trage rețeta aici</p>
                    <p className="text-xs text-gray-400 mt-1">sau folosește butoanele de mai jos</p>
                  </div>
                  <div className="flex gap-3 w-full max-w-sm">
                    <button
                      onClick={() => cameraInputRef.current?.click()}
                      className="flex-1 flex items-center justify-center gap-2 bg-rose-500 text-white font-semibold text-sm py-3 rounded-xl hover:bg-rose-600 active:scale-[0.97] transition-all shadow-sm"
                    >
                      <Camera className="w-4 h-4" />
                      <span className="hidden sm:inline">Fotografiază</span>
                      <span className="sm:hidden">Cameră</span>
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 flex items-center justify-center gap-2 border-2 border-rose-400 text-rose-500 font-semibold text-sm py-3 rounded-xl hover:bg-rose-50 active:scale-[0.97] transition-all"
                    >
                      <FolderOpen className="w-4 h-4" />
                      Din galerie
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">Formate acceptate: JPG, PNG, HEIC, WEBP</p>
                </div>

                {scanState === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-700">Eroare la scanare</p>
                      <p className="text-xs text-red-500 mt-1">{errorMsg}</p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* SCANNING STATE */}
            {scanState === 'scanning' && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {previewUrl && (
                  <div className="w-full h-64 overflow-hidden">
                    <img src={previewUrl} alt="Rețetă" className="w-full h-full object-contain bg-gray-50" />
                  </div>
                )}
                <div className="flex flex-col items-center gap-3 py-8">
                  <div className="w-14 h-14 bg-rose-50 rounded-full flex items-center justify-center">
                    <Loader2 className="w-7 h-7 text-rose-500 animate-spin" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-900">FII FIT AI analizează rețeta...</p>
                    <p className="text-xs text-gray-400 mt-1">Se extrag medicamentele și informațiile</p>
                  </div>
                </div>
              </div>
            )}

            {/* RESULT - image preview */}
            {scanState === 'result' && previewUrl && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="w-full h-64 overflow-hidden">
                  <img src={previewUrl} alt="Rețetă scanată" className="w-full h-full object-contain bg-gray-50" />
                </div>
                <div className="px-4 py-3 flex items-center justify-between border-t border-gray-50">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-semibold text-green-700">Rețetă detectată cu succes</span>
                  </div>
                  <button onClick={resetScan} className="text-xs text-gray-400 hover:text-gray-600">
                    Scanează alta
                  </button>
                </div>
              </div>
            )}

            {/* How it works - shown in idle */}
            {scanState === 'idle' && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Cum funcționează?</h3>
                <div className="space-y-3">
                  {[
                    { step: '1', text: 'Fotografiezi sau încarci o imagine cu rețeta medicală' },
                    { step: '2', text: 'FII FIT AI analizează și extrage toate medicamentele' },
                    { step: '3', text: 'Poți edita/corecta orice informație greșită înainte de salvare' },
                    { step: '4', text: 'Salvezi medicamentele direct în profilul tău medical' },
                  ].map(({ step, text }) => (
                    <div key={step} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-rose-600">{step}</span>
                      </div>
                      <p className="text-sm text-gray-600">{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN - Results */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-700">
                {scanState === 'result' ? `Medicamente detectate (${editableMeds.length})` : 'Rezultat scanare'}
              </h2>
              {scanState === 'result' && (
                <button
                  onClick={addMed}
                  className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-200 px-3 py-1.5 rounded-lg hover:bg-rose-100 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Adaugă medicament
                </button>
              )}
            </div>

            {/* Placeholder when no result */}
            {(scanState === 'idle' || scanState === 'scanning' || scanState === 'error') && (
              <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-10 flex flex-col items-center justify-center gap-3 min-h-[300px]">
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center">
                  <Upload className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-sm text-gray-400 text-center">
                  {scanState === 'scanning'
                    ? 'Se procesează rețeta...'
                    : 'Rezultatele vor apărea aici după scanare'}
                </p>
              </div>
            )}

            {/* RESULT STATE */}
            {scanState === 'result' && prescription && (
              <>
                {/* Doctor info */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-base font-bold text-gray-900">
                        {prescription.doctorName || 'Medic necunoscut'}
                      </p>
                      {prescription.specialty && (
                        <p className="text-sm text-gray-500 mt-0.5">{prescription.specialty}</p>
                      )}
                      {prescription.prescriptionDate && (
                        <p className="text-xs text-gray-400 mt-1">
                          Data: {formatDate(prescription.prescriptionDate)}
                        </p>
                      )}
                    </div>
                    <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <p className="text-sm font-bold text-rose-400">Rx</p>
                    </div>
                  </div>

                  {/* Edit hint */}
                  <div className="flex items-center gap-2 bg-blue-50 rounded-xl px-3 py-2 mb-3">
                    <Pencil className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                    <p className="text-xs text-blue-600">
                      Apasă <strong>✏️</strong> pe orice medicament pentru a corecta informațiile greșite
                    </p>
                  </div>

                  {/* Medications list - editable */}
                  <div className="space-y-2">
                    {editableMeds.map((med, idx) => (
                      <div key={idx}>
                        {editingIdx === idx && editDraft ? (
                          /* EDIT MODE */
                          <div className="bg-rose-50 border-2 border-rose-300 rounded-xl p-3 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <div className="col-span-2">
                                <label className="text-xs font-semibold text-gray-500 mb-1 block">Nume medicament *</label>
                                <input
                                  type="text"
                                  value={editDraft.name}
                                  onChange={e => setEditDraft({ ...editDraft, name: e.target.value })}
                                  placeholder="ex: Metformin 500mg"
                                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-300"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-500 mb-1 block">Doză *</label>
                                <input
                                  type="text"
                                  value={editDraft.dose}
                                  onChange={e => setEditDraft({ ...editDraft, dose: e.target.value })}
                                  placeholder="ex: 500mg, 1 comprimat"
                                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-300"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-500 mb-1 block">Frecvență *</label>
                                <input
                                  type="text"
                                  value={editDraft.frequency}
                                  onChange={e => setEditDraft({ ...editDraft, frequency: e.target.value })}
                                  placeholder="ex: 3x/zi, 1x/zi"
                                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-300"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-500 mb-1 block">Cantitate</label>
                                <input
                                  type="text"
                                  value={editDraft.quantity}
                                  onChange={e => setEditDraft({ ...editDraft, quantity: e.target.value })}
                                  placeholder="ex: 60 comprimate"
                                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-300"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-500 mb-1 block">Observații</label>
                                <input
                                  type="text"
                                  value={editDraft.notes || ''}
                                  onChange={e => setEditDraft({ ...editDraft, notes: e.target.value })}
                                  placeholder="ex: după masă"
                                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-300"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 pt-1">
                              <button
                                onClick={saveEdit}
                                disabled={!editDraft.name.trim()}
                                className="flex-1 flex items-center justify-center gap-1.5 bg-rose-500 text-white text-xs font-semibold py-2 rounded-lg hover:bg-rose-600 disabled:opacity-50"
                              >
                                <Save className="w-3.5 h-3.5" />
                                Salvează
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="flex-1 flex items-center justify-center gap-1.5 bg-white border border-gray-200 text-gray-600 text-xs font-semibold py-2 rounded-lg hover:bg-gray-50"
                              >
                                <X className="w-3.5 h-3.5" />
                                Anulează
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* VIEW MODE */
                          <div
                            className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${
                              selectedMeds.has(idx) ? 'bg-rose-50 border-rose-200' : 'bg-gray-50 border-gray-100'
                            }`}
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                                <Pill className="w-4 h-4 text-rose-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900 truncate">{med.name || '—'}</p>
                                <p className="text-xs text-gray-500">
                                  {med.quantity && `${med.quantity} • `}{med.frequency || '—'}
                                </p>
                                {med.notes && <p className="text-xs text-gray-400 italic">{med.notes}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                              {/* Edit button */}
                              <button
                                onClick={() => startEdit(idx)}
                                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                                title="Editează"
                              >
                                <Pencil className="w-3.5 h-3.5 text-gray-400 hover:text-blue-500" />
                              </button>
                              {/* Delete button */}
                              <button
                                onClick={() => deleteMed(idx)}
                                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-colors"
                                title="Șterge"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                              </button>
                              {/* Select button */}
                              <button
                                onClick={() => {
                                  const newSet = new Set(selectedMeds);
                                  if (newSet.has(idx)) newSet.delete(idx);
                                  else newSet.add(idx);
                                  setSelectedMeds(newSet);
                                }}
                                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                                  selectedMeds.has(idx)
                                    ? 'bg-rose-500 border-rose-500 text-white'
                                    : 'bg-white border-gray-200 text-gray-500 hover:border-rose-300'
                                }`}
                              >
                                {selectedMeds.has(idx) ? '✓' : '+'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {editableMeds.length === 0 && (
                      <div className="text-center py-6 text-gray-400">
                        <Pill className="w-8 h-8 mx-auto mb-2 text-gray-200" />
                        <p className="text-sm">Niciun medicament detectat</p>
                        <button onClick={addMed} className="mt-2 text-xs text-rose-500 font-semibold hover:underline">
                          + Adaugă manual
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reminder toggle */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                      {reminderEnabled ? (
                        <Bell className="w-5 h-5 text-amber-500" />
                      ) : (
                        <BellOff className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Reminder reînnoire</p>
                      <p className="text-xs text-gray-400">Te anunțăm când se termină medicamentul</p>
                    </div>
                  </div>
                  <button
                    onClick={handleReminderToggle}
                    className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                      reminderEnabled ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                        reminderEnabled ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Save to profile button */}
                {savedToProfile ? (
                  <div className="flex items-center justify-center gap-2 bg-green-50 border border-green-200 rounded-2xl py-4">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span className="text-sm font-semibold text-green-700">
                      Medicamentele au fost salvate în profil!
                    </span>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowSaveModal(true)}
                    disabled={selectedMeds.size === 0 || editingIdx !== null}
                    className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-semibold text-sm text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                    style={{ background: 'linear-gradient(135deg, #e11d48 0%, #0f766e 100%)' }}
                  >
                    <Plus className="w-4 h-4" />
                    Salvează {selectedMeds.size} {selectedMeds.size === 1 ? 'medicament' : 'medicamente'} în profil
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileInput}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileInput}
      />

      {/* Save to profile modal */}
      {showSaveModal && prescription && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">Salvează în profil medical</h3>
              <button onClick={() => setShowSaveModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <p className="text-sm text-gray-500">
              {selectedMeds.size} {selectedMeds.size === 1 ? 'medicament selectat' : 'medicamente selectate'} vor fi adăugate la lista ta de medicamente.
            </p>

            <div className="space-y-2 max-h-48 overflow-y-auto">
              {Array.from(selectedMeds).sort((a, b) => a - b).map(idx => {
                const med = editableMeds[idx];
                if (!med) return null;
                return (
                  <div key={idx} className="flex items-center gap-3 bg-rose-50 rounded-xl px-3 py-2.5">
                    <Pill className="w-4 h-4 text-rose-400 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{med.name}</p>
                      <p className="text-xs text-gray-500">{med.dose} • {med.frequency}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowSaveModal(false)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Anulează
              </button>
              <button
                onClick={handleSaveToProfile}
                disabled={savingToProfile}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm text-white hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #e11d48 0%, #0f766e 100%)' }}
              >
                {savingToProfile ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                {savingToProfile ? 'Se salvează...' : 'Confirmă'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
